/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskPresetsRequest,
  ListRiskPresetsSecurity,
} from "../models/operations/listriskpresets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskPresetsQuery,
  prefetchRiskPresets,
  queryKeyRiskPresets,
  RiskPresetsQueryData,
} from "./riskPresets.core.js";
export {
  buildRiskPresetsQuery,
  prefetchRiskPresets,
  queryKeyRiskPresets,
  type RiskPresetsQueryData,
};

export type RiskPresetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskPresets risk
 *
 * @remarks
 * Return the use-case presets a risk policy can start from. Each preset resolves to a complete create payload (policy type, detectors, action, severity and, for prompt-based presets, the judge instruction) so the dashboard and the Platform MCP offer the same starting points.
 */
export function useRiskPresets(
  request?: ListRiskPresetsRequest | undefined,
  security?: ListRiskPresetsSecurity | undefined,
  options?: QueryHookOptions<RiskPresetsQueryData, RiskPresetsQueryError>,
): UseQueryResult<RiskPresetsQueryData, RiskPresetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskPresetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskPresets risk
 *
 * @remarks
 * Return the use-case presets a risk policy can start from. Each preset resolves to a complete create payload (policy type, detectors, action, severity and, for prompt-based presets, the judge instruction) so the dashboard and the Platform MCP offer the same starting points.
 */
export function useRiskPresetsSuspense(
  request?: ListRiskPresetsRequest | undefined,
  security?: ListRiskPresetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskPresetsQueryData,
    RiskPresetsQueryError
  >,
): UseSuspenseQueryResult<RiskPresetsQueryData, RiskPresetsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskPresetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskPresetsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskPresetsQueryData,
): RiskPresetsQueryData | undefined {
  const key = queryKeyRiskPresets(...queryKeyBase);

  return client.setQueryData<RiskPresetsQueryData>(key, data);
}

export function invalidateRiskPresets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "presets", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskPresets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "presets", "list"],
  });
}
